import { c, msgid, ngettext } from 'ttag';

import { DataScope, MarketStatus } from '../../graphql/__generated__/schema';

/** A dash, never an empty cell: a blank reads as a loading row. */
export const EMPTY = '-';

export const formatDate = (value: string | null | undefined): string =>
    value ? new Date(value).toLocaleDateString() : EMPTY;

export const formatDateTime = (value: string | null | undefined): string =>
    value ? new Date(value).toLocaleString() : EMPTY;

/** `ALL` is not "every activity": it is the absence of a personal restriction. */
export const dataScopeLabel = (scope: DataScope | null | undefined, restrictedCount: number): string => {
    if (scope === DataScope.Restricted) {
        return ngettext(
            msgid`Restricted to ${restrictedCount} activity`,
            `Restricted to ${restrictedCount} activities`,
            restrictedCount
        );
    }
    return c('User perimeter').t`The whole account`;
};

/** state null: the pool did not answer; empty factors is an answer, nothing registered. */
export const mfaLabel = (
    state: { factors: string[]; preferred?: string | null } | null | undefined,
    enabledAt: string | null | undefined
): string => {
    if (!state) {
        return c('User mfa').t`State not read from the pool`;
    }
    if (state.factors.length === 0) {
        return c('User mfa').t`No factor registered`;
    }

    const named = state.factors.map((factor) => {
        if (factor === 'SOFTWARE_TOKEN_MFA') {
            return c('User mfa').t`Authenticator app`;
        }
        if (factor === 'EMAIL_OTP') {
            return c('User mfa').t`Code sent by email`;
        }
        if (factor === 'SMS_MFA') {
            return c('User mfa').t`Code sent by SMS`;
        }
        // Rendered as itself rather than folded into a catch-all bucket.
        return factor;
    });
    const factors = named.join(', ');

    if (!enabledAt) {
        return factors;
    }
    const since = formatDate(enabledAt);

    return c('User mfa').t`${factors} since ${since}`;
};

/** How a market's end is named, or null while it has not ended. Named by status, not isClosed. */
export const marketEndLabel = (status: MarketStatus | null | undefined): string | null => {
    switch (status) {
        case MarketStatus.Cancelled:
            return c('Market status').t`Cancelled`;
        case MarketStatus.Expired:
            return c('Market status').t`Expired`;
        case MarketStatus.Completed:
            return c('Market status').t`Finished`;
        case MarketStatus.Settled:
            return c('Market status').t`Settled`;
        default:
            return null;
    }
};

export interface PerimeterActivity {
    id: string;
    kind: string;
    name: string | null;
    marketId: string | null;
    marketName: string | null;
}

export interface PerimeterGroup {
    key: string;
    label: string;
    activities: { id: string; name: string | null }[];
}

export const groupPerimeter = (activities: PerimeterActivity[]): PerimeterGroup[] => {
    const needs = activities.filter((activity) => activity.kind === 'need');
    const markets = new Map<string, PerimeterGroup>();
    const noMarket: { id: string; name: string | null }[] = [];
    for (const activity of activities) {
        if (activity.kind !== 'service') {
            continue;
        }
        if (activity.marketId === null) {
            noMarket.push({ id: activity.id, name: activity.name });
            continue;
        }
        const group = markets.get(activity.marketId) ?? {
            key: activity.marketId,
            label: activity.marketName ?? c('Users').t`Market without a name`,
            activities: [],
        };
        group.activities.push({ id: activity.id, name: activity.name });
        markets.set(activity.marketId, group);
    }

    const groups: PerimeterGroup[] = [];
    if (needs.length > 0) {
        groups.push({
            key: 'needs',
            label: c('Users').t`Needs`,
            activities: needs.map((need) => ({ id: need.id, name: need.name })),
        });
    }
    groups.push(...[...markets.values()].sort((first, second) => first.label.localeCompare(second.label)));
    if (noMarket.length > 0) {
        groups.push({ key: 'no-market', label: c('Users').t`Without a market`, activities: noMarket });
    }
    return groups;
};
